// Embassy Influence (03 §4.1, GDD 02 §4.4; numbers baseline.yaml → influence). Stored per system in
// milli-units, produced lazily like resources and capped at `storage_cap_hours_of_production` hours of
// production. The boost is cut from the slice (S-3). Unowned systems store no Influence (decision log).
import { type Balance, type GalaxySpeed, MS_PER_HOUR } from '@stars-and-ships/balance';
import { MILLI } from './stockpile.js';

export interface InfluenceEconomy {
  /** Milli-units per hour, scaled by the galaxy speed (G-40). */
  ratePerH: bigint;
  /** Milli-units; caps are not scaled by the galaxy speed. */
  cap: bigint;
}

export const NO_INFLUENCE: InfluenceEconomy = Object.freeze({ ratePerH: 0n, cap: 0n });

const H = BigInt(MS_PER_HOUR);

export function influenceEconomy(
  balance: Balance,
  embassyLevel: number,
  speed: GalaxySpeed
): InfluenceEconomy {
  const base = balance.influence.production_per_h[embassyLevel];
  if (base === undefined) {
    throw new RangeError(`influence.production_per_h has no entry for level ${embassyLevel}`);
  }
  const perHour = BigInt(base) * MILLI;
  return {
    ratePerH: speed.ratePerH(perHour),
    cap: perHour * BigInt(balance.influence.storage_cap_hours_of_production),
  };
}

/** Stored Influence at `toMs`, with the same telescoping accrual as `evaluateResources`. */
export function evaluateInfluence(
  stored: bigint,
  economy: InfluenceEconomy,
  fromMs: number,
  toMs: number
): bigint {
  if (toMs <= fromMs || stored >= economy.cap) return stored;
  const next =
    stored + (economy.ratePerH * BigInt(toMs)) / H - (economy.ratePerH * BigInt(fromMs)) / H;
  return next > economy.cap ? economy.cap : next;
}
